// @figma file=nq2oTuNpAeyLOoF0tXtO32 node=tbd name="Spinner" set="shadcn-studio-pro-v5" verified=tbd

import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Base ─────────────────────────────────────────────────────────────────────

export const spinnerBase =
  'animate-spin rounded-full border-solid border-current/20 border-t-current shrink-0';

// ─── Variant config ───────────────────────────────────────────────────────────

export const spinnerVariantConfig = {
  variants: {
    /**
     * Size — matches Figma `Size` dimension.
     * sm → 16px · md → 24px (default) · lg → 32px · xl → 48px
     */
    size: {
      sm: 'size-4  border-2',
      md: 'size-6  border-2',
      lg: 'size-8  border-[3px]',
      xl: 'size-12 border-4',
    },
    /**
     * Color — maps to semantic tokens.
     * primary (default) · secondary · muted · info · success · warning · destructive
     */
    variant: {
      primary: 'text-primary',
      secondary: 'text-secondary-foreground',
      muted: 'text-muted-foreground',
      info: 'text-info',
      success: 'text-success',
      warning: 'text-warning',
      destructive: 'text-destructive',
    },
  },
  defaultVariants: {
    size: 'md' as const,
    variant: 'primary' as const,
  },
};

const spinnerVariants = cva(spinnerBase, spinnerVariantConfig);

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SpinnerProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof spinnerVariants> {
  /** Screen-reader announcement text */
  label?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const Spinner = React.forwardRef<HTMLSpanElement, SpinnerProps>(
  ({ className, size, variant, label = 'Loading…', ...props }, ref) => (
    <span
      ref={ref}
      role="status"
      aria-label={label}
      className={cn(spinnerVariants({ size, variant }), className)}
      {...props}
    >
      <span className="sr-only">{label}</span>
    </span>
  ),
);
Spinner.displayName = 'Spinner';

export { Spinner, spinnerVariants };
